import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmCard } from '../../../shared/components/ConfirmCard';
import { sandboxTransmissionActionsEnabled } from '../../../shared/commercialActions';
import { requestNotificationsRefresh } from '../../notifications/useStoredNotifications';
import { AckDialog } from '../../check-batch/components/AckDialog';
import type { CheckNumberEdit } from '../../check-batch/components/CheckTable';
import { ReadinessStep, type ReadinessCopy } from '../../check-batch/components/ReadinessStep';
import { ResultDialog } from '../../check-batch/components/ResultDialog';
import { Stepper, type StepLabels } from '../../check-batch/components/Stepper';
import { TrialBanner } from '../../check-batch/components/TrialBanner';
import { plural } from '../../check-batch/format';
import '../../check-batch/check-batch.css';
import { contractorBatchGateway as defaultGateway, type ContractorBatchGateway } from '../index';
import { describeContractorFailure } from '../errors';
import type { AddLineInput, ContractorBatch, ContractorBatchLine, ContractorPaymentSettings, UpdateLineInput } from '../types';
import { AddPaymentDialog, type PaymentDialogMode } from '../components/AddPaymentDialog';
import { ContractorApproved } from '../components/ContractorApproved';
import { ContractorCheckNote } from '../components/ContractorNotes';
import { ContractorDraftDialog } from '../components/ContractorDraftDialog';
import { ContractorReview } from '../components/ContractorReview';
import { ContractorSummaryBar } from '../components/ContractorSummaryBar';
import { ContractorTable } from '../components/ContractorTable';
import { ThresholdDialog } from '../components/ThresholdDialog';
import '../pay-contractors.css';

type Step = 1 | 2 | 3 | 4;
type Dialog =
  | { kind: 'remove'; lineIds: string[]; label: string }
  | { kind: 'removed' }
  | { kind: 'ack' }
  | { kind: 'draftSaved' }
  | { kind: 'discard'; returnTo: 'draftSaved' | null }
  | { kind: 'discarded' }
  | { kind: 'threshold' };

interface View {
  batch: ContractorBatch | null;
  step: Step;
  /** A draft the admin explicitly saved earlier was found on entry: ask what to do with it. */
  existingDraft: boolean;
}

type Load = { kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'ready' };
type SettingsLoad = { kind: 'loading' } | { kind: 'unavailable' } | { kind: 'loaded'; settings: ContractorPaymentSettings };

const STEP_LABELS: StepLabels = ['Pay Contractors', 'Readiness Check', 'Review Payment', 'Approved'];
const READINESS_COPY: ReadinessCopy = {
  backLabel: 'Back to Pay Contractors',
  continueWithWarnings: 'Continue to Review Payment',
  continueClean: 'Continue to Review Payment',
};

function newKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `k-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

function asStep(n: number): Step {
  return n >= 3 ? 3 : n <= 1 ? 1 : 2;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** The payment-method tabs. Write Check is the whole flow; Direct Deposit stays visible but disabled because EPN has no contractor bank account model yet. */
function MethodTabs() {
  return (
    <>
      <div className="wc-tabs" role="tablist" aria-label="Payment method">
        <button type="button" role="tab" id="pay-contractor-tab-dd" aria-selected="false" aria-disabled="true" disabled className="wc-tab wc-tab--disabled" aria-describedby="pay-contractor-dd-note">
          Direct Deposit
        </button>
        <button type="button" role="tab" id="pay-contractor-tab-check" aria-selected="true" className="wc-tab">Write Check</button>
      </div>
      <p id="pay-contractor-dd-note" className="wc-muted" data-testid="direct-deposit-note">
        Paying contractors by direct deposit is coming later. For now, contractors are paid by check.
      </p>
    </>
  );
}

/**
 * EPN-250 — Pay Contractors, Write Check. Starts directly on this page (no Custom Pay Run): choose a pay date, add contractor payments, run the readiness
 * check, review and approve. The server owns every rule (eligibility, amounts, check numbers and their resequencing, readiness, approval); this component
 * asks and shows. Approving only records an approval: it prints, mails and pays nothing, and the approve action is not offered at all in a production
 * bundle (EPN-98).
 */
export function PayContractorsPage({ gateway = defaultGateway }: { gateway?: ContractorBatchGateway }) {
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [view, setView] = useState<View>({ batch: null, step: 1, existingDraft: false });
  const [nonce, setNonce] = useState(0);

  const [payDate, setPayDate] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [edit, setEdit] = useState<CheckNumberEdit | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [paymentDialog, setPaymentDialog] = useState<PaymentDialogMode | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmedVersion, setConfirmedVersion] = useState<number | null>(null);
  const [settings, setSettings] = useState<SettingsLoad>({ kind: 'loading' });
  const approveKey = useRef<{ version: number; key: string } | null>(null);

  // ---- load: the open draft, if any ---------------------------------------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const drafts = await gateway.listBatches('Draft');
      return drafts[0] ?? null;
    })()
      .then((draft) => {
        if (cancelled) return;
        if (draft) {
          setView({ batch: draft, step: 1, existingDraft: draft.lastSavedAtUtc !== null });
          setPayDate(draft.payDate);
          setPeriodStart(draft.periodStartDate ?? '');
          setPeriodEnd(draft.periodEndDate ?? '');
        } else {
          setView({ batch: null, step: 1, existingDraft: false });
        }
        setLoad({ kind: 'ready' });
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoad({ kind: 'error', message: describeContractorFailure(err, 'The contractor payments couldn’t be loaded. Try again.').message });
      });
    return () => {
      cancelled = true;
    };
  }, [gateway, nonce]);

  useEffect(() => {
    let cancelled = false;
    gateway
      .getSettings()
      .then((s) => {
        if (!cancelled) setSettings({ kind: 'loaded', settings: s });
      })
      .catch(() => {
        if (!cancelled) setSettings({ kind: 'unavailable' });
      });
    return () => {
      cancelled = true;
    };
  }, [gateway, nonce]);

  const batch = view.batch;
  const step: Step = batch ? view.step : 1;
  const lines = useMemo(() => (batch ? batch.lines.filter((l) => l.included) : []), [batch]);
  const selectedIncluded = useMemo(() => lines.filter((l) => selected.has(l.id)), [lines, selected]);
  const confirmed = batch !== null && confirmedVersion === batch.version;
  const takenIds = useMemo(() => new Set(lines.map((l) => l.contractorId)), [lines]);

  const setBatch = useCallback((next: ContractorBatch, nextStep?: Step) => {
    setView((v) => ({ ...v, batch: next, step: nextStep ?? v.step }));
  }, []);

  /** Runs one server action; a refusal is shown in plain language, and a version conflict reloads the latest batch. */
  const act = useCallback(
    async (work: () => Promise<void>, fallback: string): Promise<boolean> => {
      setBusy(true);
      setActionError(null);
      try {
        await work();
        return true;
      } catch (err) {
        const failure = describeContractorFailure(err, fallback);
        setActionError(failure.message);
        if (failure.versionConflict && batch) {
          try {
            setBatch(await gateway.getBatch(batch.id));
          } catch {
            /* the message above already tells the admin to retry */
          }
        }
        return false;
      } finally {
        setBusy(false);
      }
    },
    [batch, gateway, setBatch],
  );

  // ---- header (pay date + period) --------------------------------------------------------------------------------------------------------------
  const periodIncomplete = (periodStart === '') !== (periodEnd === '');
  const periodReversed = periodStart !== '' && periodEnd !== '' && periodEnd < periodStart;
  const headerError = periodIncomplete ? 'Enter both period dates, or clear them.' : periodReversed ? 'The period end is before its start.' : null;
  const pastPayDate = payDate !== '' && payDate < todayIso();

  async function commitHeader() {
    if (!batch || payDate === '' || headerError) return;
    const changed = payDate !== batch.payDate || (periodStart || null) !== batch.periodStartDate || (periodEnd || null) !== batch.periodEndDate;
    if (!changed) return;
    await act(async () => {
      setBatch(await gateway.updateDates(batch.id, { payDate, periodStartDate: periodStart || null, periodEndDate: periodEnd || null }, batch.version));
    }, 'The pay date couldn’t be saved. Try again.');
  }

  function clearFilter() {
    setPeriodStart('');
    setPeriodEnd('');
    if (!batch) setPayDate('');
    else if (batch.periodStartDate !== null) {
      void act(async () => {
        setBatch(await gateway.updateDates(batch.id, { payDate: batch.payDate, periodStartDate: null, periodEndDate: null }, batch.version));
      }, 'The period couldn’t be cleared. Try again.');
    }
  }

  // ---- handlers -----------------------------------------------------------------------------------------------------------------------------------
  function toggle(lineId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => (lines.length > 0 && lines.every((l) => prev.has(l.id)) ? new Set() : new Set(lines.map((l) => l.id))));
  }

  async function savePayment(input: AddLineInput | UpdateLineInput, lineId: string | null): Promise<string | null> {
    try {
      let current = batch;
      if (!current) {
        if (payDate === '' || headerError) return 'Choose a pay date first.';
        current = await gateway.createBatch({ payDate, periodStartDate: periodStart || null, periodEndDate: periodEnd || null });
        setPayDate(current.payDate);
        setPeriodStart(current.periodStartDate ?? '');
        setPeriodEnd(current.periodEndDate ?? '');
        setBatch(current, 1);
      }
      const next = lineId
        ? await gateway.updateLine(current.id, lineId, input as UpdateLineInput, current.version)
        : await gateway.addLine(current.id, input as AddLineInput, current.version);
      setBatch(next, 1);
      setConfirmedVersion(null);
      setPaymentDialog(null);
      return null;
    } catch (err) {
      const failure = describeContractorFailure(err, 'The payment couldn’t be saved. Try again.');
      if (failure.versionConflict && batch) {
        try {
          setBatch(await gateway.getBatch(batch.id));
        } catch {
          /* message stands */
        }
      }
      return failure.message;
    }
  }

  function startEdit(line: ContractorBatchLine) {
    setActionError(null);
    setEdit({ lineId: line.id, value: line.checkNumber, error: null, saving: false });
  }

  async function commitEdit() {
    if (!batch || !edit) return;
    const value = edit.value.trim();
    if (value === '') {
      setEdit({ ...edit, error: 'Enter a check number.' });
      return;
    }
    const line = batch.lines.find((l) => l.id === edit.lineId);
    if (line && line.checkNumber.toUpperCase() === value.toUpperCase()) {
      setEdit(null);
      return;
    }
    setEdit({ ...edit, saving: true, error: null });
    try {
      setBatch(await gateway.editCheckNumber(batch.id, edit.lineId, value, batch.version));
      setEdit(null);
    } catch (err) {
      const failure = describeContractorFailure(err, 'The check number couldn’t be saved. Try again.');
      if (failure.versionConflict) {
        setEdit(null);
        setActionError(failure.message);
        try {
          setBatch(await gateway.getBatch(batch.id));
        } catch {
          /* message above stands */
        }
      } else {
        setEdit({ lineId: edit.lineId, value: edit.value, saving: false, error: failure.message });
      }
    }
  }

  async function confirmRemove() {
    if (!batch || dialog?.kind !== 'remove') return;
    const ids = dialog.lineIds;
    const ok = await act(async () => {
      setBatch(await gateway.removeLines(batch.id, ids, batch.version));
    }, 'The contractor couldn’t be removed from this batch. Try again.');
    if (ok) {
      setSelected(new Set());
      setDialog({ kind: 'removed' });
    }
  }

  async function runReadiness() {
    if (!batch) return;
    await act(async () => {
      setBatch(await gateway.runReadiness(batch.id, batch.version), 2);
    }, 'The readiness check couldn’t be run. Try again.');
  }

  function continueFromReadiness() {
    if (!batch?.readiness || batch.readiness.isStale || !batch.readiness.canContinue) return;
    const warnings = batch.readiness.warnings;
    const ack = batch.warningAcknowledgement;
    const covered = warnings.length === 0 || (ack?.acknowledged && warnings.every((w) => ack.warningIds.includes(w.id)));
    if (covered) {
      setActionError(null);
      setView((v) => ({ ...v, step: 3 }));
      return;
    }
    setActionError(null);
    setDialog({ kind: 'ack' });
  }

  async function acknowledge() {
    if (!batch?.readiness) return;
    const ids = batch.readiness.warnings.map((w) => w.id);
    const ok = await act(async () => {
      setBatch(await gateway.acknowledgeWarnings(batch.id, ids, batch.version), 3);
    }, 'The acknowledgement couldn’t be saved. Try again.');
    if (ok) setDialog(null);
  }

  async function saveDraft() {
    if (!batch) return;
    const ok = await act(async () => {
      setBatch(await gateway.saveDraft(batch.id, step === 4 ? 3 : step, batch.version));
    }, 'The draft couldn’t be saved. Try again.');
    if (ok) setDialog({ kind: 'draftSaved' });
  }

  async function discardDraft() {
    if (!batch) return;
    const ok = await act(async () => {
      await gateway.discard(batch.id, batch.version);
    }, 'The draft couldn’t be discarded. Try again.');
    if (ok) {
      setSelected(new Set());
      setConfirmedVersion(null);
      setDialog({ kind: 'discarded' });
    }
  }

  function afterDiscard() {
    setDialog(null);
    setPayDate('');
    setPeriodStart('');
    setPeriodEnd('');
    setView({ batch: null, step: 1, existingDraft: false });
  }

  async function approve() {
    if (!batch || !confirmed) return;
    if (approveKey.current?.version !== batch.version) approveKey.current = { version: batch.version, key: newKey() };
    const idempotencyKey = approveKey.current.key;
    await act(async () => {
      setBatch(await gateway.approve(batch.id, batch.version, idempotencyKey), 4);
      requestNotificationsRefresh();
    }, 'The batch couldn’t be approved. Try again.');
  }

  function backToPayContractors() {
    // The approved batch is finished: start from an empty page so the admin can prepare another one.
    setSelected(new Set());
    setConfirmedVersion(null);
    setPayDate('');
    setPeriodStart('');
    setPeriodEnd('');
    setActionError(null);
    setView({ batch: null, step: 1, existingDraft: false });
  }

  async function saveThreshold(value: number | null, percent: number | null) {
    const saved = await gateway.saveSettings(value, percent);
    setSettings({ kind: 'loaded', settings: saved });
    setDialog(null);
  }

  // ---- derived: why Approve can't be used yet -----------------------------------------------------------------------------------------------
  let approveBlockedReason: string | null = null;
  if (batch) {
    const r = batch.readiness;
    if (!r || r.isStale) approveBlockedReason = 'The readiness check is out of date. Run it again before approving.';
    else if (r.blockers.length > 0) approveBlockedReason = 'Blocking issues remain. Resolve them and run the readiness check again.';
    else if (r.warnings.length > 0 && !(batch.warningAcknowledgement?.acknowledged && r.warnings.every((w) => batch.warningAcknowledgement?.warningIds.includes(w.id)))) {
      approveBlockedReason = 'The warnings from the readiness check have not been acknowledged.';
    } else if (batch.totals.contractorsIncluded === 0) approveBlockedReason = 'There are no contractors in this batch.';
  }

  const preview = batch?.isPreview ?? true;
  const canAddPayment = payDate !== '' && headerError === null && !busy;

  return (
    <div className="wc pay-contractors" data-testid="pay-contractors">
      {preview && <TrialBanner />}
      <h2 className="wc-title">Pay Contractors</h2>

      <section className="card card--wide wc-card" aria-label="Write Check contractor payment batch">
        <Stepper current={step} labels={STEP_LABELS} ariaLabel="Pay Contractors progress" />

        {actionError && <p className="error wc-action-error" role="alert" data-testid="flow-error">{actionError}</p>}

        {load.kind === 'loading' && <p className="wc-muted" role="status">Loading contractor payments…</p>}
        {load.kind === 'error' && (
          <div role="alert">
            <p className="error" data-testid="load-error">{load.message}</p>
            <button type="button" className="wc-btn wc-btn--outline" onClick={() => { setLoad({ kind: 'loading' }); setNonce((n) => n + 1); }}>Try again</button>
          </div>
        )}

        {load.kind === 'ready' && step === 1 && (
          <>
            <div className="wc-filters">
              <label className="wc-field">
                <span>Pay Date <span aria-hidden="true">*</span></span>
                <input type="date" value={payDate} required aria-required="true" disabled={busy} onChange={(e) => setPayDate(e.target.value)} onBlur={() => void commitHeader()} />
              </label>
              <label className="wc-field">
                <span>Payment period start</span>
                <input type="date" value={periodStart} disabled={busy} onChange={(e) => setPeriodStart(e.target.value)} onBlur={() => void commitHeader()} />
              </label>
              <label className="wc-field">
                <span>Payment period end</span>
                <input type="date" value={periodEnd} disabled={busy} onChange={(e) => setPeriodEnd(e.target.value)} onBlur={() => void commitHeader()} />
              </label>
              <button type="button" className="wc-btn wc-btn--dark" disabled={busy || (periodStart === '' && periodEnd === '' && (batch !== null || payDate === ''))} onClick={clearFilter}>Clear Filter</button>
            </div>
            {headerError && <p className="error" role="alert" data-testid="header-error">{headerError}</p>}
            {pastPayDate && <p className="wc-warn" role="status" data-testid="past-date-warning">This pay date is in the past. You can still continue, but check that it is the date you intend.</p>}
            <p className="wc-muted" data-testid="schedule-note">Contractors are not on pay schedules, so there is no pay schedule to choose. Pick the pay date; the payment period is optional.</p>

            <MethodTabs />
            <ContractorCheckNote />
            <ContractorSummaryBar totals={batch?.totals ?? { contractorsIncluded: 0, paymentTotal: 0, checkTotal: 0 }} />

            <div className="wc-footer wc-footer--review wc-toolbar">
              <div>
                <button type="button" className="wc-btn wc-btn--green" disabled={!canAddPayment} aria-describedby={payDate === '' ? 'add-payment-hint' : undefined} onClick={() => setPaymentDialog({ kind: 'add' })}>
                  Add contractor payment
                </button>
                {payDate === '' && <span id="add-payment-hint" className="wc-muted wc-hint"> Choose a pay date to start.</span>}
              </div>
              {settings.kind === 'loaded' && (
                <p className="wc-muted wc-threshold" data-testid="threshold-line">
                  1099 threshold: {settings.settings.form1099ThresholdAmount === null ? 'not set' : `$${settings.settings.form1099ThresholdAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}{' '}
                  <button type="button" className="wc-link" onClick={() => setDialog({ kind: 'threshold' })}>Change</button>
                </p>
              )}
            </div>

            {batch ? (
              <ContractorTable
                batch={batch}
                selectedIds={selected}
                edit={edit}
                disabled={busy}
                onToggle={toggle}
                onToggleAll={toggleAll}
                onStartEdit={startEdit}
                onChangeEdit={(value) => setEdit((e) => (e ? { ...e, value, error: null } : e))}
                onCommitEdit={() => void commitEdit()}
                onCancelEdit={() => setEdit(null)}
                onEditPayment={(line) => setPaymentDialog({ kind: 'edit', line })}
                onRemoveRow={(line) => setDialog({ kind: 'remove', lineIds: [line.id], label: line.contractorName })}
              />
            ) : (
              <p className="wc-empty" role="status" data-testid="empty-batch">
                No contractor payments yet. Choose a pay date, then use “Add contractor payment” to pick who to pay by check.
              </p>
            )}

            <p className="wc-muted wc-edit-hint">To change a contractor’s name, pay method, W-9 or 1099 setup, open the contractor from the Contractors page. Amounts are edited here with “Edit”.</p>
            <div className="wc-footer wc-footer--review">
              <button type="button" className="wc-btn wc-btn--outline" onClick={() => navigate('/people/contractors')}>Go to Contractors</button>
              <div className="wc-footer__actions">
                {selectedIncluded.length > 0 && (
                  <button
                    type="button"
                    className="wc-btn wc-btn--danger-outline"
                    disabled={busy}
                    onClick={() =>
                      setDialog({
                        kind: 'remove',
                        lineIds: selectedIncluded.map((l) => l.id),
                        label: selectedIncluded.length === 1 ? (selectedIncluded[0]?.contractorName ?? 'this contractor') : plural(selectedIncluded.length, 'contractor', 'contractors'),
                      })
                    }
                  >
                    Remove from Batch ({selectedIncluded.length})
                  </button>
                )}
                <button type="button" className="wc-btn wc-btn--primary" disabled={busy || lines.length === 0} onClick={() => void runReadiness()}>
                  Run Readiness Check
                </button>
              </div>
            </div>
          </>
        )}

        {load.kind === 'ready' && batch && step === 2 && (
          <ReadinessStep
            batch={batch}
            busy={busy}
            copy={READINESS_COPY}
            onBack={() => setView((v) => ({ ...v, step: 1 }))}
            onRerun={() => void runReadiness()}
            onContinue={continueFromReadiness}
          />
        )}

        {load.kind === 'ready' && batch && step === 3 && (
          <ContractorReview
            batch={batch}
            confirmed={confirmed}
            onConfirmedChange={(v) => setConfirmedVersion(v ? batch.version : null)}
            blockedReason={approveBlockedReason}
            approveAvailable={sandboxTransmissionActionsEnabled}
            busy={busy}
            onBack={() => setView((v) => ({ ...v, step: 2 }))}
            onDiscard={() => setDialog({ kind: 'discard', returnTo: null })}
            onSaveDraft={() => void saveDraft()}
            onApprove={() => void approve()}
            onRecheck={() => setView((v) => ({ ...v, step: 2 }))}
          />
        )}

        {load.kind === 'ready' && batch && step === 4 && <ContractorApproved batch={batch} onBack={backToPayContractors} />}
      </section>

      {/* ---- dialogs ---- */}
      {paymentDialog && (
        <AddPaymentDialog mode={paymentDialog} takenContractorIds={takenIds} onClose={() => setPaymentDialog(null)} onSubmit={savePayment} />
      )}
      {dialog?.kind === 'threshold' && settings.kind === 'loaded' && (
        <ThresholdDialog current={settings.settings.form1099ThresholdAmount} currentPercent={settings.settings.form1099ApproachingPercent ?? null} effectivePercent={settings.settings.effectiveApproachingPercent ?? null} onClose={() => setDialog(null)} onSave={saveThreshold} />
      )}
      {dialog?.kind === 'remove' && (
        <ConfirmCard
          title="Remove from Batch?"
          body={`Remove ${dialog.label} from this payment batch? This only excludes the contractor from this batch. Their contractor record is not changed or deleted, and the batch totals are recalculated.`}
          cancelLabel="No, Keep It"
          confirmLabel="Remove from Batch"
          confirmTone="danger"
          busy={busy}
          error={actionError}
          onCancel={() => {
            setActionError(null);
            setDialog(null);
          }}
          onConfirm={() => void confirmRemove()}
        />
      )}
      {dialog?.kind === 'removed' && (
        <ResultDialog
          title="Removed from the Batch"
          body="The contractor was removed from this payment batch only. The batch totals were updated, and you can continue with the remaining contractors."
          buttonLabel="Back to Pay Contractors"
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'ack' && batch?.readiness && (
        <AckDialog
          warnings={batch.readiness.warnings}
          busy={busy}
          error={actionError}
          onCancel={() => {
            setActionError(null);
            setDialog(null);
          }}
          onConfirm={() => void acknowledge()}
        />
      )}
      {dialog?.kind === 'draftSaved' && batch && (
        <ContractorDraftDialog
          batch={batch}
          variant="saved"
          busy={busy}
          onBackToDashboard={() => navigate('/dashboards/admin-dashboard')}
          onDiscard={() => setDialog({ kind: 'discard', returnTo: 'draftSaved' })}
          onContinue={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'discard' && (
        <ConfirmCard
          title="Discard Draft?"
          body="Are you sure you want to discard this draft? The saved payment batch will be removed and its check numbers released. This action cannot be undone."
          cancelLabel="No, Keep Editing"
          confirmLabel="Yes, Discard Draft"
          confirmTone="danger"
          busy={busy}
          error={actionError}
          onCancel={() => {
            setActionError(null);
            // Cancelling goes back to where the admin came from: the saved-draft card, or the page.
            setDialog(dialog.returnTo === 'draftSaved' ? { kind: 'draftSaved' } : null);
          }}
          onConfirm={() => void discardDraft()}
        />
      )}
      {dialog?.kind === 'discarded' && (
        <ResultDialog
          title="Draft Successfully Discarded!"
          body="The contractor payment batch draft has been removed. Nothing was approved, printed, mailed or paid."
          buttonLabel="Go to Pay Contractors"
          onClose={afterDiscard}
        />
      )}
      {view.existingDraft && batch && dialog === null && paymentDialog === null && load.kind === 'ready' && step === 1 && (
        <ContractorDraftDialog
          batch={batch}
          variant="existing"
          busy={busy}
          onBackToDashboard={() => navigate('/dashboards/admin-dashboard')}
          onDiscard={() => setDialog({ kind: 'discard', returnTo: null })}
          onContinue={() => setView((v) => ({ ...v, existingDraft: false, step: v.batch ? asStep(v.batch.currentStep) : 1 }))}
        />
      )}
    </div>
  );
}

